import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

/**
 * Initial load query — fetches folder metadata, permissions, and the first page
 * of both folder and non-folder children in a single network request.
 *
 * Overfetches 100 items per stream into a client-side buffer so the UI can
 * display 40 items at a time without waiting for another network round-trip.
 */
export const FolderOverviewDualStreamFirstPageQuery: GraphQLTaggedNode = graphql`
	query DualStreamQueriesFirstPageQuery(
		$cloudId: ID!
		$parentId: ID!
		$spaceKey: String
		$contentId: ID!
		$first: Int = 100
		$includeDevDocsSyncStatus: Boolean = false
	) {
		ptpage(id: $parentId, status: [CURRENT, DRAFT]) {
			id
			title
			type
			subType
		}
		singleContent(id: $contentId) {
			operations {
				operation
			}
			contentProperties {
				latest {
					coverPicture
				}
			}
			# Smart-folder source is read from singleContent, not ptpage: the
			# smartFolderProperties content property is only surfaced by the
			# singleContent resolver (ptpage.properties returns it empty).
			properties(keys: ["smartFolderProperties"]) {
				nodes {
					id
					key
					value
				}
			}
			devDocsProperties: properties(keys: ["devdocsProperties"])
				@include(if: $includeDevDocsSyncStatus) {
				nodes {
					id
					key
					value
				}
			}
		}
		folders: confluence_contentTreeChildren(
			cloudId: $cloudId
			parentId: $parentId
			spaceKey: $spaceKey
			type: FOLDER
			first: $first
		) {
			nodes {
				id
				title
				status
				type
				subType
				emojiTitlePublished
				links {
					webui
				}
				properties {
					nodes {
						id
						key
						value
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
		content: confluence_contentTreeChildren(
			cloudId: $cloudId
			parentId: $parentId
			spaceKey: $spaceKey
			type: CONTENT
			first: $first
		) {
			nodes {
				id
				title
				status
				type
				subType
				emojiTitlePublished
				links {
					webui
				}
				properties {
					nodes {
						id
						key
						value
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

/**
 * Pagination query — fetches the next page of either folders or content,
 * determined by the `type` variable passed at call time.
 *
 * Reused for both streams — only the `type` and `after` cursor differ.
 */
export const FolderOverviewDualStreamPaginateQuery: GraphQLTaggedNode = graphql`
	query DualStreamQueriesPaginateQuery(
		$cloudId: ID!
		$parentId: ID!
		$spaceKey: String
		$type: ConfluenceContentTypeFilter!
		$first: Int = 100
		$after: String
	) {
		confluence_contentTreeChildren(
			cloudId: $cloudId
			parentId: $parentId
			spaceKey: $spaceKey
			type: $type
			first: $first
			after: $after
		) {
			nodes {
				id
				title
				status
				type
				subType
				emojiTitlePublished
				links {
					webui
				}
				properties {
					nodes {
						id
						key
						value
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;
